import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChannelEditorField } from './ChannelEditorField'
import type { ContentFieldKey } from './channel-constants'

interface SeoField {
  key: ContentFieldKey
  value: string
  masterValue: string
  isInherited: boolean
}

interface ChannelEditorSeoSectionProps {
  fields: SeoField[]
  isMasterEditor: boolean
  allInherited: boolean
  productId: string
  channelId: string
  onChange: (key: ContentFieldKey, value: string) => void
  onToggleInherit: (key: ContentFieldKey) => void
}

export function ChannelEditorSeoSection({
  fields, isMasterEditor, allInherited, productId, channelId, onChange, onToggleInherit,
}: ChannelEditorSeoSectionProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const fieldMap = Object.fromEntries(fields.map((f) => [f.key, f])) as Record<ContentFieldKey, SeoField>

  const renderField = (key: ContentFieldKey) => {
    const f = fieldMap[key]
    if (!f) return null
    const isMultiline = key === 'SeoMetaDescription'
    return (
      <ChannelEditorField key={key} label={key} value={f.value} masterValue={f.masterValue}
        isInherited={f.isInherited} isMasterEditor={isMasterEditor}
        multiline={isMultiline} rows={2}
        productId={productId} channelId={channelId}
        onChange={(v) => onChange(key, v)} onToggleInherit={() => onToggleInherit(key)} />
    )
  }

  return (
    <div className="border-t border-neutral-200 dark:border-neutral-800 pt-3">
      <button onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-xs font-medium text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors">
        <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
        </svg>
        <span>{t('productContent.channels.seoFields')}</span>
        {allInherited && (
          <span className="text-2xs text-neutral-600 ml-1">
            {t('productContent.channels.allFromMaster')}
          </span>
        )}
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          {renderField('SeoTitleTag')}
          {renderField('SeoMetaDescription')}
          <div className="grid grid-cols-2 gap-3">
            {renderField('SeoMetaKeywords')}
            {renderField('SeoPath')}
          </div>
        </div>
      )}
    </div>
  )
}
